import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Mail, User as UserIcon, Calendar, Lock, Edit, Save, X, Briefcase, Building, Target, Heart, BookOpen, Award, FileText, Smartphone, Globe, Zap } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { getAllCoachProfiles } from '../../lib/coachProfiles';
import { supportNeedToCoachMap } from '../../lib/recommendations';
import { updateProfessionalProfile, updateCoachingPreferences, getUserProfile, updateNudgeSettings } from '../../lib/api';
import { useAuth } from '@clerk/clerk-react';
import { useQueryClient, useQuery } from '@tanstack/react-query';

const AccountSettings = () => {
  const { user } = useUser();
  const { getToken, isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const { data: profile } = useCurrentUserProfile();

  // Fetch extended profile data (job description)
  const { data: extendedProfile } = useQuery({
    queryKey: ['user-profile-extended'],
    enabled: !!isSignedIn,
    queryFn: async () => {
      const token = await getToken();
      if (!token) return null;
      return getUserProfile(token);
    }
  });

  // Basic profile states
  const [isEditingName, setIsEditingName] = useState(false);
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');

  // Professional information state
  const [currentRole, setCurrentRole] = useState(profile?.current_role || '');
  const [jobDescription, setJobDescription] = useState('');
  const [yearsExperience, setYearsExperience] = useState(profile?.years_of_experience || '');
  const [industry, setIndustry] = useState(profile?.industry || '');

  // Coaching preferences state
  const [goalStatement, setGoalStatement] = useState(profile?.goal_statement || '');
  const [developmentGoals, setDevelopmentGoals] = useState(profile?.development_goals || []);
  const [supportNeed, setSupportNeed] = useState(profile?.support_need || '');
  const [preferredCoach, setPreferredCoach] = useState(profile?.preferred_coach || '');
  const [coachPersona, setCoachPersona] = useState(profile?.coach_persona || '');
  const [phoneNumber, setPhoneNumber] = useState(profile?.phone_number || '');
  const [maskedPhoneNumber, setMaskedPhoneNumber] = useState(profile?.masked_phone_number || '');
  const [timezone, setTimezone] = useState(profile?.timezone || '');

  // Editing states
  const [isEditingProfessional, setIsEditingProfessional] = useState(false);
  const [isEditingCoaching, setIsEditingCoaching] = useState(false);
  const [isSavingProfessional, setIsSavingProfessional] = useState(false);
  const [isSavingCoaching, setIsSavingCoaching] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);

  // Password state
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Update local state when profile data loads
  useEffect(() => {
    if (profile) {
      setCurrentRole(profile.current_role || '');
      setYearsExperience(profile.years_of_experience || '');
      setIndustry(profile.industry || '');
      setGoalStatement(profile.goal_statement || '');
      setDevelopmentGoals(profile.development_goals || []);
      setSupportNeed(profile.support_need || '');
      setPreferredCoach(profile.preferred_coach || '');
      setCoachPersona(profile.coach_persona || '');
      setPhoneNumber(profile.phone_number || '');
      setMaskedPhoneNumber(profile.masked_phone_number || '');
      setTimezone(profile.timezone || '');
    }

    if (extendedProfile) {
      setJobDescription(extendedProfile.job_description || '');
    }
  }, [profile, extendedProfile]);

  const handleNameUpdate = async () => {
    if (!user) return;
    try {
      setIsSavingName(true);
      // Update name in Clerk
      await user.update({ firstName, lastName });

      // Update phone and timezone in our backend
      const token = await getToken();
      if (token) {
        await updateNudgeSettings({
          phoneNumber,
          timezone
        }, token);
      }

      setIsEditingName(false);
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      queryClient.invalidateQueries({ queryKey: ['users-me'] });
      success('Success', 'Your profile details have been updated.');
    } catch (error) {
      console.error('Error updating profile:', error);
      showError('Error', 'Failed to update your profile. Please try again.');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleProfessionalUpdate = async () => {
    try {
      setIsSavingProfessional(true);
      const token = await getToken();
      if (!token) throw new Error('Authentication required');

      await updateProfessionalProfile({
        currentRole,
        yearsExperience,
        industry,
        goalStatement, // Keep goal statement in sync
        jobDescription
      } as any, token);

      // Invalidate queries to refresh user data everywhere
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile-extended'] });
      queryClient.invalidateQueries({ queryKey: ['users-me'] });

      setIsEditingProfessional(false);
      success('Success', 'Your professional information has been updated.');
    } catch (error: any) {
      console.error('Failed to update professional information:', error);
      showError('Error', error.message || 'Failed to update professional information. Please try again.');
    } finally {
      setIsSavingProfessional(false);
    }
  };

  const handleCoachingUpdate = async () => {
    try {
      setIsSavingCoaching(true);
      const token = await getToken();
      if (!token) throw new Error('Authentication required');

      await updateCoachingPreferences({
        goalStatement,
        developmentGoals,
        supportNeed,
        preferredCoach
      }, token);

      await updateNudgeSettings({
        coachPersona,
        phoneNumber,
        timezone
      }, token);

      // Invalidate queries to refresh user data everywhere
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile-extended'] });
      queryClient.invalidateQueries({ queryKey: ['users-me'] });

      setIsEditingCoaching(false);
      success('Success', 'Your coaching preferences have been updated.');
    } catch (error: any) {
      console.error('Failed to update coaching preferences:', error);
      showError('Error', error.message || 'Failed to update coaching preferences. Please try again.');
    } finally {
      setIsSavingCoaching(false);
    }
  };

  const coachProfiles = getAllCoachProfiles();
  const supportNeedOptions = Object.keys(supportNeedToCoachMap);

  return (
    <div className="space-y-6">
      {/* Profile Details */}
      <div className="rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm p-5 sm:p-6 border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-semibold text-lg text-slate-900 tracking-tight">Profile Details</h3>
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <button 
                onClick={handleNameUpdate} 
                disabled={isSavingName}
                className="p-2 text-green-600 hover:bg-green-50 rounded-xl transition-colors disabled:opacity-50" 
                title="Save Profile"
              >
                {isSavingName ? <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" /> : <Save size={18} />}
              </button>
              <button 
                onClick={() => setIsEditingName(false)} 
                disabled={isSavingName}
                className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors disabled:opacity-50" 
                title="Cancel"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <button onClick={() => setIsEditingName(true)} className="p-2 text-slate-400 hover:bg-slate-50 hover:text-royal-blue rounded-xl transition-all" title="Edit Profile"><Edit size={18} /></button>
          )}
        </div>
        <div className="space-y-6">
          {/* Username */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <UserIcon className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Username</span>
            </div>
            {isEditingName ? (
              <div className="flex flex-col xs:flex-row items-center gap-2 w-full sm:w-auto">
                <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full xs:w-32 p-2 border rounded-xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none" placeholder="First Name" />
                <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full xs:w-32 p-2 border rounded-xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none" placeholder="Last Name" />
              </div>
            ) : (
              <span className="text-slate-600 pl-8 sm:pl-0">{user?.firstName} {user?.lastName}</span>
            )}
          </div>

          {/* Email */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Email</span>
            </div>
            <span className="text-slate-600 pl-8 sm:pl-0 truncate">{user?.primaryEmailAddress?.emailAddress}</span>
          </div>

          {/* Member Since */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Member Since</span>
            </div>
            <span className="text-slate-600 pl-8 sm:pl-0">{user?.createdAt?.toLocaleDateString()}</span>
          </div>

          {/* Phone Number */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <Smartphone className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Phone Number</span>
            </div>
            {isEditingName ? (
              <input 
                type="tel" 
                value={phoneNumber} 
                onChange={(e) => setPhoneNumber(e.target.value)} 
                className="w-full sm:w-64 p-2 border rounded-xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none" 
                placeholder="+1 234 567 8900" 
              />
            ) : (
              <span className="text-slate-600 pl-8 sm:pl-0 font-mono tracking-wider">{phoneNumber || 'Not specified'}</span>
            )}
          </div>

          {/* Timezone */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <Globe className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Timezone</span>
            </div>
            {isEditingName ? (
              <select 
                value={timezone} 
                onChange={(e) => setTimezone(e.target.value)} 
                className="w-full sm:w-64 p-2 border rounded-xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none bg-white"
              >
                <option value="UTC">UTC</option>
                {/* Dynamic timezone list with fallback */}
                {typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl ? (
                  Intl.supportedValuesOf('timeZone').map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))
                ) : (
                  <option value={Intl.DateTimeFormat().resolvedOptions().timeZone}>
                    {Intl.DateTimeFormat().resolvedOptions().timeZone}
                  </option>
                )}
              </select>
            ) : (
              <span className="text-slate-600 pl-8 sm:pl-0">{timezone || 'Not specified'}</span>
            )}
          </div>
        </div>
      </div>

      {/* Professional Information */}
      <div className="rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm p-5 sm:p-6 border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-semibold text-lg text-slate-900 tracking-tight">Professional Information</h3>
          {isEditingProfessional ? (
            <div className="flex items-center gap-2">
              <button
                onClick={handleProfessionalUpdate}
                disabled={isSavingProfessional}
                className="p-2 text-green-600 hover:bg-green-50 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={isSavingProfessional ? 'Saving...' : 'Save'}
              >
                {isSavingProfessional ? (
                  <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
                ) : (
                  <Save size={18} />
                )}
              </button>
              <button
                onClick={() => setIsEditingProfessional(false)}
                disabled={isSavingProfessional}
                className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Cancel"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <button onClick={() => setIsEditingProfessional(true)} className="p-2 text-slate-400 hover:bg-slate-50 hover:text-royal-blue rounded-xl transition-all" title="Edit"><Edit size={18} /></button>
          )}
        </div>
        <div className="space-y-6">
          {/* Current Role */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <Briefcase className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Current Role</span>
            </div>
            {isEditingProfessional ? (
              <input
                type="text"
                value={currentRole}
                onChange={(e) => setCurrentRole(e.target.value)}
                className="w-full sm:w-64 p-2 border rounded-xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none"
                placeholder="e.g. Senior Product Manager"
              />
            ) : (
              <span className="text-slate-600 pl-8 sm:pl-0 font-medium">{currentRole || 'Not specified'}</span>
            )}
          </div>

          {/* Job Description */}
          <div className="space-y-3 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Job Description</span>
            </div>
            {isEditingProfessional ? (
              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                className="w-full p-4 border rounded-2xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none min-h-[120px]"
                rows={4}
                placeholder="Describe your current role, responsibilities, and what you do day-to-day..."
              />
            ) : (
              <p className="text-sm text-slate-600 pl-8 leading-relaxed bg-slate-50/50 p-4 rounded-2xl border border-slate-100 italic">
                {jobDescription || 'Not specified'}
              </p>
            )}
          </div>

          {/* Years of Experience */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <Award className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Years of Experience</span>
            </div>
            {isEditingProfessional ? (
              <select
                value={yearsExperience}
                onChange={(e) => setYearsExperience(e.target.value)}
                className="w-full sm:w-64 p-2 border rounded-xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none appearance-none bg-white"
                aria-label="Years of experience"
              >
                <option value="">Select experience</option>
                <option value="0-2">0-2 years</option>
                <option value="3-5">3-5 years</option>
                <option value="6-10">6-10 years</option>
                <option value="11-15">11-15 years</option>
                <option value="16-20">16-20 years</option>
                <option value="20+">20+ years</option>
              </select>
            ) : (
              <span className="text-slate-600 pl-8 sm:pl-0 font-medium">{yearsExperience || 'Not specified'}</span>
            )}
          </div>

          {/* Industry */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <Building className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Industry</span>
            </div>
            {isEditingProfessional ? (
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full sm:w-64 p-2 border rounded-xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none appearance-none bg-white"
                aria-label="Industry"
              >
                <option value="">Select industry</option>
                <option value="Technology">Technology</option>
                <option value="Finance">Finance</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Education">Education</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Retail">Retail</option>
                <option value="Consulting">Consulting</option>
                <option value="Other">Other</option>
              </select>
            ) : (
              <span className="text-slate-600 pl-8 sm:pl-0 font-medium">{industry || 'Not specified'}</span>
            )}
          </div>
        </div>
      </div>

      {/* Coaching Preferences */}
      <div className="rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm p-5 sm:p-6 border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-semibold text-lg text-slate-900 tracking-tight">Coaching Preferences</h3>
          {isEditingCoaching ? (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCoachingUpdate}
                disabled={isSavingCoaching}
                className="p-2 text-green-600 hover:bg-green-50 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={isSavingCoaching ? 'Saving...' : 'Save'}
              >
                {isSavingCoaching ? (
                  <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
                ) : (
                  <Save size={18} />
                )}
              </button>
              <button
                onClick={() => setIsEditingCoaching(false)}
                disabled={isSavingCoaching}
                className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Cancel"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <button onClick={() => setIsEditingCoaching(true)} className="p-2 text-slate-400 hover:bg-slate-50 hover:text-royal-blue rounded-xl transition-all" title="Edit"><Edit size={18} /></button>
          )}
        </div>
        <div className="space-y-6">
          {/* Goal Statement */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Goal Statement</span>
            </div>
            {isEditingCoaching ? (
              <textarea
                value={goalStatement}
                onChange={(e) => setGoalStatement(e.target.value)}
                className="w-full p-4 border rounded-2xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none min-h-[100px]"
                rows={3}
                placeholder="What are your main coaching goals and aspirations?"
              />
            ) : (
              <p className="text-sm text-slate-600 pl-8 leading-relaxed bg-slate-50/50 p-4 rounded-2xl border border-slate-100 italic">
                {goalStatement || 'Not specified'}
              </p>
            )}
          </div>

          {/* Development Goals */}
          <div className="space-y-3 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <BookOpen className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Development Goals</span>
            </div>
            {isEditingCoaching ? (
              <textarea
                value={developmentGoals.join('\n')}
                onChange={(e) => setDevelopmentGoals(e.target.value.split('\n').filter(g => g.trim()))}
                className="w-full p-4 border rounded-2xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none min-h-[100px]"
                rows={3}
                placeholder="List your key development goals (one per line)"
              />
            ) : (
              <div className="pl-8">
                {developmentGoals.length > 0 ? (
                  <ul className="list-disc list-inside text-sm text-slate-600 space-y-2 bg-slate-50/50 p-4 rounded-2xl border border-slate-100">
                    {developmentGoals.map((goal, index) => (
                      <li key={index}>{goal}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-600 bg-slate-50/50 p-4 rounded-2xl border border-slate-100 italic">Not specified</p>
                )}
              </div>
            )}
          </div>

          {/* Support Needs */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <Heart className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Support Needs</span>
            </div>
            {isEditingCoaching ? (
              <select
                value={supportNeed}
                onChange={(e) => setSupportNeed(e.target.value)}
                className="w-full sm:w-64 p-2 border rounded-xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none appearance-none bg-white"
                aria-label="Support needs selection"
              >
                <option value="">Select support need</option>
                {supportNeedOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-slate-600 pl-8 sm:pl-0 font-medium">{supportNeed || 'Not specified'}</span>
            )}
          </div>

          {/* Preferred Coach */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <UserIcon className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Preferred Coach</span>
            </div>
            {isEditingCoaching ? (
              <select
                value={preferredCoach}
                onChange={(e) => setPreferredCoach(e.target.value)}
                className="w-full sm:w-64 p-2 border rounded-xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none appearance-none bg-white"
                aria-label="Preferred coach selection"
              >
                <option value="">Select preferred coach</option>
                {coachProfiles.map((coach) => (
                  <option key={coach.id} value={coach.id}>
                    {coach.name} - {coach.expertise}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-slate-600 pl-8 sm:pl-0 font-medium truncate">
                {preferredCoach ? coachProfiles.find(c => c.id === preferredCoach)?.name || preferredCoach : 'Not specified'}
              </span>
            )}
          </div>

          {/* Coach Persona (Engagement) */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-slate-50 pt-4 sm:pt-0 sm:border-0">
            <div className="flex items-center gap-3">
              <Zap className="h-5 w-5 text-slate-400" />
              <span className="font-medium text-slate-700">Coach Persona (Nudges)</span>
            </div>
            {isEditingCoaching ? (
              <select
                value={coachPersona}
                onChange={(e) => setCoachPersona(e.target.value)}
                className="w-full sm:w-64 p-2 border rounded-xl text-sm focus:ring-2 focus:ring-royal-blue/20 outline-none appearance-none bg-white"
                aria-label="Coach persona selection"
              >
                <option value="">Select persona</option>
                <option value="rajesh">Rajesh (Strategic & Direct)</option>
                <option value="samuel">Samuel (Warm & Encouraging)</option>
                <option value="priya">Priya (Empathetic & Insightful)</option>
                <option value="meera">Meera (Pragmatic & Focused)</option>
                <option value="aisha">Aisha (Inspirational & Bold)</option>
              </select>
            ) : (
              <span className="text-slate-600 pl-8 sm:pl-0 font-medium">
                {coachPersona ? coachProfiles.find(p => p.id === coachPersona)?.name || coachPersona : 'Not specified'}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="rounded-xl bg-white/80 backdrop-blur-sm shadow-sm p-6">
        <h3 className="font-semibold text-lg mb-4">Security</h3>
        {user?.passwordEnabled ? (
          isUpdatingPassword ? (
            <div className="space-y-4">
              <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="w-full p-2 border rounded" placeholder="Current Password" />
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full p-2 border rounded" placeholder="New Password" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setIsUpdatingPassword(false)} className="font-medium text-sm text-gray-600 hover:underline">Cancel</button>
                <button onClick={async () => {
                  try {
                    await user.updatePassword({ currentPassword, newPassword });
                    setIsUpdatingPassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    success('Success', 'Your password has been updated.');
                  } catch (error) {
                    console.error('Error updating password:', error);
                    showError('Error', 'Failed to update your password. Please check your current password and try again.');
                  }
                }} className="font-medium text-sm text-blue-600 hover:underline">Save Password</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Lock className="h-5 w-5 text-gray-500" />
                <span className="font-medium">Password</span>
              </div>
              <button onClick={() => setIsUpdatingPassword(true)} className="font-medium text-sm text-blue-600 hover:underline">Update Password</button>
            </div>
          )
        ) : (
          <p className="text-sm text-gray-600">You are signed in with a social account. Manage your password through your identity provider.</p>
        )}
      </div>
    </div>
  );
};

export default AccountSettings;
